import React, { useState } from 'react';
import { useRealtorContext } from '../context/RealtorContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Hero from '../components/layout/Hero';
import Section from '../components/layout/Section';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import PropertyCard from '../components/property/PropertyCard';
import FeatureCard from '../components/ui/FeatureCard';
import TestimonialCard from '../components/ui/TestimonialCard';
import ImageModal from '../components/ui/ImageModal';

const HomePage: React.FC = () => {
  const { currentUser } = useRealtorContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalImages, setModalImages] = useState<string[]>([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [modalTitle, setModalTitle] = useState('');
  
    const handleImageClick = (images: string[], title: string, clickedImage: string) => {
      setModalImages(images);
      setModalTitle(title);
      setCurrentImageIndex(images.indexOf(clickedImage));
      setIsModalOpen(true);
    };
  
    const handleCloseModal = () => {
      setIsModalOpen(false);
      setModalImages([]);
      setCurrentImageIndex(0);
      setModalTitle('');
    };
  
    const handleNextImage = () => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % modalImages.length);
    };
  
    const handlePrevImage = () => {
      setCurrentImageIndex((prevIndex) =>
        (prevIndex - 1 + modalImages.length) % modalImages.length
      );
    };
  
  // Featured properties
  const featuredProperties = [
    {
      id: 1,
      title: '2 Bedroom Semi-detached Bungalow',
      price: 30500000,
      location: 'Unique Solar Gardens Estate, SHEGO District, KARU',
      bedrooms: 2,
      bathrooms: 2,
      area: 350,
      image: '/assets/photos/1.jpg',
      images: ['/assets/photos/9.jpg', '/assets/photos/1.jpg', '/assets/photos/2.jpg'],
      status: 'For Sale',
      featured: true
    },
    {
      id: 2,
      title: '2 Bedroom Semi-detached Bungalow',
      price: 30500000,
      location: 'Unique Solar Gardens Estate, SHEGO District, KARU',
      bedrooms: 3,
      bathrooms: 2,
      area: 350,
      image: '/assets/photos/2.jpg',
      images: ['/assets/photos/9.jpg', '/assets/photos/1.jpg', '/assets/photos/2.jpg'],
      status: 'For Sale',
      featured: true
    },
    {
      id: 3,
      title: 'Luxury 2 Bedroom Semi-detached Bungalow',
      price: 30500000,
      location: 'Unique Solar Gardens Estate, SHEGO District, KARU',
      bedrooms: 3,
      bathrooms: 3,
      area: 350,
      image: '/assets/photos/3.jpg',
      images: ['/assets/photos/3.jpg', '/assets/photos/1.jpg', '/assets/photos/2.jpg'],
      status: 'For Sale',
      featured: true
    }
  ];
  
  // Estate features
  const estateFeatures = [
    {
      icon: '⚡',
      title: '24/7 Solar Power',
      description: 'Dedicated 24 hours electricity powered by mini grid solar plant.'
    },
    {
      icon: '💧',
      title: '24/7 Running Water',
      description: 'Constant water supply throughout the estate for all residents.'
    },
    {
      icon: '🔒',
      title: '24/7 Security',
      description: 'Round-the-clock security surveillance of the entire estate.'
    },
    {
      icon: '💡',
      title: 'Solar Street Lights',
      description: 'Well-lit streets powered by renewable solar energy.'
    },
    {
      icon: '🛣️',
      title: 'Quality Road Network',
      description: 'Good interlocking internal road network with proper drainage systems.'
    },
    {
      icon: '♻️',
      title: 'Waste Management',
      description: 'Efficient facility management and waste disposal unit.'
    }
  ];
  
  // Testimonials
  const testimonials = [
    {
      name: 'Okafor Emeka',
      role: 'Property Owner',
      image: '/assets/photos/4.jpg',
      quote: 'Buying a home in Unique Solar Gardens Estate was the best decision I made. The 24/7 solar power is reliable and has significantly reduced my energy costs.'
    },
    {
      name: 'Adewale Segun',
      role: 'Resident',
      image: '/assets/photos/5.jpg',
      quote: 'The serene environment and constant utilities make this estate stand out. I love the sense of community and the eco-friendly approach to living.'
    },
    {
      name: 'Abubakar Mohammed',
      role: 'Investor',
      image: '/assets/photos/6.jpg',
      quote: 'As an investor, the value proposition of Unique Solar Gardens Estate is unmatched. The solar-powered infrastructure is a game-changer in today\'s real estate market.'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow">
        {/* Hero Section with Video Background */}
        <Hero
          title="Welcome to Unique Solar Gardens Estate"
          subtitle="A premier solar-powered estate in SHEGO District, KARU, offering modern living with sustainable energy solutions."
          backgroundVideo="/assets/videos/solar-panels.mp4"
          height="large"
          buttonText="View Properties"
          buttonLink="/properties"
          secondaryButtonText="Contact Us"
          secondaryButtonLink="/contact"
        />
        
        {/* Introduction Section */}
        <Section background="white" className="pt-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-primary mb-6">Discover Sustainable Living at Unique Solar Gardens Estate</h2>
              <p className="text-gray-700 mb-6 text-lg">
                Located in the heart of SHEGO District, KARU, Unique Solar Gardens Estate offers a perfect blend of modern comfort and sustainable living. Our solar-powered homes provide reliable electricity, reducing your carbon footprint while ensuring you never experience power outages.
              </p>
              <p className="text-gray-700 mb-8 text-lg">
                With Plot 18180 CADESTRAL ZONE B21 SHEGO KARU as our address, we're strategically positioned with excellent proximity to essential amenities and the Central Business District.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  variant="primary"
                  size="lg"
                  onClick={() => window.location.href = '/about'}
                >
                  Learn More
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  onClick={() => window.location.href = '/location'}
                >
                  View Location
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="/assets/photos/7.jpg"
                alt="Unique Solar Gardens Estate" 
                className="rounded-lg shadow-xl w-full h-auto"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-lg shadow-lg">
                <div className="flex items-center">
                  <div className="bg-secondary p-3 rounded-full mr-4" style={{ color: 'black' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-800 font-bold">100% Solar Powered</p>
                    <p className="text-gray-600">Sustainable & Reliable</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>
        
        {/* Featured Properties Section */}
        <Section background="light" className="pt-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary mb-4">Featured Properties</h2>
            <p className="text-gray-700 max-w-3xl mx-auto text-left">
              Explore our selection of premium properties in Unique Solar Gardens Estate, designed for comfort, sustainability, and modern living.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProperties.map(property => (
              <PropertyCard 
                key={property.id}
                image={property.image}
                title={property.title}
                price={`₦${property.price.toLocaleString()}`}
                bedrooms={property.bedrooms}
                bathrooms={property.bathrooms}
                area={`${property.area} sqm`}
                tag={property.featured ? "Featured" : undefined}
                onImageClick={(clickedImage) => handleImageClick(property.images, property.title, clickedImage)}
              />
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button 
              variant="primary"
              size="lg"
              onClick={() => window.location.href = '/properties'}
            >
              View All Properties
            </Button>
          </div>
        </Section>
        
        {/* Estate Features Section */}
        <Section background="white" className="pt-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary mb-4">Estate Facilities</h2>
            <p className="text-gray-700 max-w-3xl mx-auto">
              Unique Solar Gardens Estate offers a range of premium facilities designed to enhance your living experience and provide maximum comfort.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {estateFeatures.map((feature, index) => (
              <FeatureCard 
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
            ))}
          </div>
          
          <div className="text-center mt-12 mb-6">
            <Button 
              variant="primary"
              size="lg"
              onClick={() => window.location.href = '/facilities'}
            >
              Explore All Facilities
            </Button>
          </div>
        </Section>
        
        {/* Payment Options Section */}
        <Section background="primary" className="pt-16">
          {/* @ts-ignore */}
          <div className="text-left mb-12" align='left'>
            <h2 className="text-3xl font-bold text-white mb-4">Flexible Payment Options</h2>
            <p className="text-white/90 max-w-full mx-auto text-left">
              We offer various payment plans to make your dream home more accessible. Choose the option that best suits your financial situation.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="p-8 hover:transform hover:scale-105 transition-all duration-300">
              <div className="text-center">
                <div className="bg-secondary/20 text-secondary w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-primary mb-2">Outright Payment</h3>
                <p className="text-gray-600 mb-6">
                  Make a one-time full payment and get immediate access to your new home.
                </p>
                <div className="bg-gray-100 p-4 rounded-lg mb-6">
                  <p className="text-gray-800 font-bold">2 Bedroom Bungalow</p>
                  <p className="text-secondary text-2xl font-bold">₦30.5M</p>
                </div>
                <div className="bg-gray-100 p-4 rounded-lg">
                  <p className="text-gray-800 font-bold">3 Bedroom Bungalow</p>
                  <p className="text-secondary text-2xl font-bold">₦35M</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-8 hover:transform hover:scale-105 transition-all duration-300">
              <div className="text-center">
                <div className="bg-secondary/20 text-secondary w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-primary mb-2">Mortgage Option</h3>
                <p className="text-gray-600 mb-6">
                  Available for NHF contributors with a 10% equity contribution deposit.
                </p>
                <ul className="text-left space-y-4 mb-6">
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">10% initial deposit</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Competitive interest rates</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Flexible repayment terms</span>
                  </li>
                </ul>
                <Button 
                  variant="secondary"
                  className="w-full"
                  onClick={() => window.location.href = '/payment'}
                >
                  Learn More
                </Button>
              </div>
            </Card>
            
            <Card className="p-8 hover:transform hover:scale-105 transition-all duration-300">
              <div className="text-center">
                <div className="bg-secondary/20 text-secondary w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-primary mb-2">Installment Plan</h3>
                <p className="text-gray-600 mb-6">
                  Flexible installment payment options available on negotiable terms.
                </p>
                <ul className="text-left space-y-4 mb-6">
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Customized payment schedule</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Based on your circumstances</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Rent-to-own options available</span>
                  </li>
                </ul>
                <Button 
                  variant="secondary"
                  className="w-full"
                  onClick={() => window.location.href = '/payment'}
                >
                  Learn More
                </Button>
              </div>
            </Card>
          </div>
        </Section>
        
        {/* Testimonials Section */}
        <Section background="light" className="pt-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary mb-4">What Our Residents Say</h2>
            <p className="text-gray-700 max-w-3xl mx-auto">
              Hear from the people who call Unique Solar Gardens Estate home and discover why they love living here.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard 
                key={index}
                authorName={testimonial.name}
                authorRole={testimonial.role}
                authorImage={testimonial.image}
                content={testimonial.quote}
              />
            ))}
          </div>
        </Section>
        
        {/* Call to Action Section */}
        <Section background="primary" className="pt-16">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Make Unique Solar Gardens Your Home?</h2>
            <p className="text-white/90 max-w-3xl mx-auto mb-8">
              Contact us today to schedule a visit or learn more about our available properties and payment options.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                variant="secondary"
                size="lg"
                onClick={() => window.location.href = '/contact'}
              >
                Contact Us
              </Button>
              <Button 
                variant="outline"
                size="lg"
                onClick={() => window.location.href = '/properties'}
              >
                View Properties
              </Button>
            </div>
          </div>
        </Section>
        
        {/* Realtor Portal CTA (if not logged in) */}
        {!currentUser && (
          <Section background="white" className="pt-16">
            <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4">Join Our Realtor Network</h2>
                  <p className="text-gray-700 mb-6">
                    Become a realtor for Unique Solar Gardens Estate and earn competitive commissions. Our multi-level affiliate system allows you to build your network and increase your earnings.
                  </p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secondary mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700">Earn up to 5% commission on direct sales</span>
                    </li>
                    <li className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secondary mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700">Build a network of up to 2 levels of affiliates</span>
                    </li>
                    <li className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secondary mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700">Access to marketing materials and property information</span>
                    </li>
                    <li className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secondary mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700">Comprehensive dashboard to track your performance</span>
                    </li>
                  </ul>
                  <Button 
                    variant="primary"
                    size="lg"
                    onClick={() => window.location.href = '/register'}
                  >
                    Become a Realtor
                  </Button>
                </div>
                
                <div className="relative">
                  <img 
                    src="/assets/photos/8.jpg"
                    alt="Realtor Network" 
                    className="rounded-lg shadow-xl w-full h-auto"
                  />
                  <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-lg shadow-lg">
                    <div className="flex items-center">
                      <div className="bg-primary text-white p-3 rounded-full mr-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                      </div>
                      <div>
                        <p className="text-gray-800 font-bold">Earn Commissions</p>
                        <p className="text-gray-600">Up to 5% per sale</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Section>
        )}
      </main>
      
      <Footer />
       {isModalOpen && (
        <ImageModal
          images={modalImages}
          currentIndex={currentImageIndex}
          onClose={handleCloseModal}
          onNext={handleNextImage}
          onPrev={handlePrevImage}
        />
      )}
    </div>
  );
};

export default HomePage;
